import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { readLocal, writeLocal } from "@/lib/storage";

export type Theme = "light" | "dark" | "system";
type Resolved = "light" | "dark";

interface ThemeState {
  theme: Theme;
  /** the theme applied: the light interface draws the day chart, the dark one the night chart */
  resolved: Resolved;
  setTheme: (t: Theme) => void;
}

const STORE = "seamap.theme";
const ThemeContext = createContext<ThemeState | null>(null);

function systemTheme(): Resolved {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const t = readLocal(STORE);
    return t === "light" || t === "dark" ? t : "system";
  });
  const [system, setSystem] = useState<Resolved>(systemTheme);

  useEffect(() => {
    const q = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!q) return;
    const on = () => setSystem(q.matches ? "dark" : "light");
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, []);

  const resolved: Resolved = theme === "system" ? system : theme;
  useEffect(() => {
    document.documentElement.classList.toggle("dark", resolved === "dark");
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    writeLocal(STORE, t === "system" ? null : t);
  }, []);

  const value = useMemo(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme]);
  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeState {
  const t = useContext(ThemeContext);
  if (!t) throw new Error("useTheme outside ThemeProvider");
  return t;
}

/** The mode of the chart that goes with the interface. */
export function useChartMode(): "day" | "night" {
  return useTheme().resolved === "dark" ? "night" : "day";
}
